import { ApolloError } from '@apollo/client';
import IconCheck from '@apollo/icons/default/IconCheck.svg';
import IconClose from '@apollo/icons/default/IconClose.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Group,
  Input,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Select,
  Spinner,
  Switch,
  Text,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';

import { Form } from 'src/components/common/form/Form';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useIsGraphIdAvailable } from 'src/hooks/useIsGraphIdAvailable';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import {
  useDXSchemaForConnectorsOnboarding,
  useHideConnectorsPreviewBadges,
} from 'src/lib/featureFlags';
import { generateUniqueString } from 'src/lib/generateUniqueString';
import { generateGraphId } from 'src/lib/graphOnboarding/helpers/utils';

import { ErrorMessage } from '../errorMessage/ErrorMessage';

import { DEFAULT_SDLS, FormValues, graphSetupValidationSchema } from './shared';

interface CreateNewSupergraphStepContentProps {
  accountId: string;
  canLoggedInUserCreateHiddenGraph: boolean;
  submitCallback: (formValues: FormValues) => void;
  graphTitle: string;
  onCancel: () => void;
  apiErrors?: (ApolloError | undefined)[];
  isProcessing?: boolean;
}

const DEFAULT_SDLS_KEYS = Object.keys(DEFAULT_SDLS);

export const CreateNewSupergraphStepContent = ({
  accountId,
  canLoggedInUserCreateHiddenGraph,
  submitCallback,
  graphTitle,
  onCancel,
  apiErrors = [],
  isProcessing = false,
}: CreateNewSupergraphStepContentProps) => {
  const shouldUseDXDefaultSubgraph = useDXSchemaForConnectorsOnboarding();

  const {
    errors,
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    isValid,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormik<FormValues>({
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    // disabling reinitialize to avoid race conditions when the user's service list is updated to include the new graph
    enableReinitialize: false,
    initialValues: {
      graphTitle,
      graphId: generateGraphId(graphTitle),
      hiddenFromUninvitedNonAdminAccountMembers: false,
      accountId,
      subgraphName: shouldUseDXDefaultSubgraph
        ? 'products'
        : (DEFAULT_SDLS_KEYS[0] ?? ''),
    },
    async onSubmit(formValues) {
      await submitCallback(formValues);
    },
    validationSchema: graphSetupValidationSchema,
  });

  const { isGraphIdAvailable, loading: isGraphIdAvailableLoading } =
    useIsGraphIdAvailable({
      accountId,
      graphId: values.graphId,
    });

  const graphTitleError = touched.graphTitle && errors.graphTitle;
  const graphIdError = touched.graphId && errors.graphId;
  const subgraphNameError = touched.subgraphName && errors.subgraphName;

  // store a unique string to add as a suffix so it doesn't regenerate when typing
  const [uniqueString, setUniqueString] = useState(generateUniqueString);

  // We want to keep the graph id in sync with graph title if the user hasn't edited yet.
  useEffect(() => {
    if (!touched.graphId) {
      setFieldValue('graphId', generateGraphId(values.graphTitle));
    }
  }, [setFieldValue, touched.graphId, values.graphTitle]);

  // If the user hasn't edited the id, we want to update the generate id so it's unique
  useEffect(() => {
    if (!isGraphIdAvailableLoading && !isGraphIdAvailable && !touched.graphId) {
      let nextId = generateGraphId(values.graphTitle, uniqueString);
      // This will catch if the unique string has already been used with this title and generate a new one
      if (nextId === values.graphId) {
        const nextUniqueString = generateUniqueString();
        setUniqueString(nextUniqueString);
        nextId = generateGraphId(values.graphTitle, nextUniqueString);
      }
      setFieldValue('graphId', nextId);
    }
  }, [
    setFieldValue,
    touched.graphId,
    values.graphTitle,
    uniqueString,
    values.graphId,
    isGraphIdAvailableLoading,
    isGraphIdAvailable,
  ]);

  const isCreatingGraphProject = isSubmitting || isProcessing;

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Connect a REST API</ModalTitle>
          <ModalDescription>
            Turn your existing REST APIs into a unified graph.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {!useHideConnectorsPreviewBadges() && <PreviewAlert />}
          {!isCreatingGraphProject && apiErrors.length ? (
            <Group className="mb-2" orientation="vertical" gap={4}>
              {apiErrors.map((err) => (
                <ErrorMessage error={err} key={err?.message} />
              ))}
            </Group>
          ) : null}
          <Group orientation="vertical" gap={6}>
            <FormControl isInvalid={!!graphTitleError} isRequired>
              <FormLabel>Graph title</FormLabel>
              <Input
                autoFocus={true}
                isDisabled={isCreatingGraphProject}
                name="graphTitle"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="e.g., Great Northern Graph"
                value={values.graphTitle}
                type="text"
              />
              <FormErrorMessage>{graphTitleError}</FormErrorMessage>
            </FormControl>

            <FormControl
              isInvalid={
                !!graphIdError ||
                (!isGraphIdAvailable && !isGraphIdAvailableLoading)
              }
              isRequired
            >
              <FormLabel>Graph ID</FormLabel>
              <Input
                isDisabled={isCreatingGraphProject}
                name="graphId"
                onBlur={handleBlur}
                onChange={(event) => {
                  setFieldTouched('graphId', true);
                  handleChange(event);
                }}
                value={values.graphId}
                type="text"
                rightElement={
                  <Tooltip
                    label={
                      isGraphIdAvailableLoading
                        ? 'Loading graph ID availability'
                        : isGraphIdAvailable
                          ? 'Graph ID available'
                          : 'This graph ID is already taken'
                    }
                  >
                    {isGraphIdAvailableLoading ? (
                      <Spinner size="tiny" />
                    ) : isGraphIdAvailable ? (
                      <IconCheck className="rounded-full text-icon-success" />
                    ) : (
                      <IconClose className="cursor-pointer rounded-full text-icon-error" />
                    )}
                  </Tooltip>
                }
              />
              <FormErrorMessage>
                {!isGraphIdAvailable
                  ? 'A graph with this ID already exists'
                  : graphIdError}
              </FormErrorMessage>
            </FormControl>

            {!shouldUseDXDefaultSubgraph && (
              <FormControl isInvalid={!!subgraphNameError} isRequired>
                <FormLabel>
                  Select an example subgraph
                  <Tooltip label="Subgraphs are the GraphQL services (or modules) that make up your supergraph. These examples are designed to help you get started with Connectors and their syntax.">
                    <IconInfo tabIndex={0} />
                  </Tooltip>
                </FormLabel>
                <Select
                  isDisabled={isCreatingGraphProject}
                  name="subgraphName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.subgraphName}
                >
                  {DEFAULT_SDLS_KEYS.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            {canLoggedInUserCreateHiddenGraph && (
              <FormControl>
                <FormLabel className="flex items-center justify-between">
                  Graph visibility
                  <Switch
                    isChecked={
                      !values.hiddenFromUninvitedNonAdminAccountMembers
                    }
                    onChange={() =>
                      setFieldValue(
                        'hiddenFromUninvitedNonAdminAccountMembers',
                        !values.hiddenFromUninvitedNonAdminAccountMembers,
                      )
                    }
                  >
                    {values.hiddenFromUninvitedNonAdminAccountMembers
                      ? 'Hidden'
                      : 'Visible'}
                  </Switch>
                </FormLabel>
                <Text size="base">
                  {values.hiddenFromUninvitedNonAdminAccountMembers
                    ? 'Your graph will be hidden from other organization members unless you add them.'
                    : 'Your graph will be visible to other members in your organization.'}
                </Text>
              </FormControl>
            )}
          </Group>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button
              variant="secondary"
              isDisabled={isCreatingGraphProject}
              type="button"
              onClick={async () => {
                ampli.cancelConnectApiClicked({
                  'API Type': 'REST',
                  OnboardingVariant: 'GraphOS-Trial',
                });
                onCancel();
              }}
            >
              Cancel
            </Button>
            <Button
              isDisabled={!isValid || !isGraphIdAvailable}
              type="submit"
              isLoading={isCreatingGraphProject}
              loadingText="Creating graph"
              onClick={async () => {
                ampli.createGraphClicked({
                  'API Type': 'REST',
                  OnboardingVariant: 'GraphOS-Trial',
                });
              }}
            >
              Create and continue
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Form>
  );
};

// generates a preview alert if the connectors feature is in preview
const PreviewAlert = () => {
  return (
    <Alert status="beta">
      This feature is currently{' '}
      <a
        href="https://www.apollographql.com/docs/graphos/reference/release-stages#preview"
        target="_blank"
        rel="noreferrer"
        className="font-semibold underline"
      >
        in preview
      </a>
      .
    </Alert>
  );
};
